import React, { useEffect, useRef, useState } from 'react';
import SectionHeader from './SectionHeader';
import useInView from '../hooks/useInView';

const EMAIL = 'yoshidon.com@gmail.com';

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="m4 7 8 6 8-6" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="1.8" y="1.8" width="12.4" height="12.4" rx="3.2" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="8" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="11.6" cy="4.4" r="0.8" fill="currentColor" />
    </svg>
  );
}

function LineIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 3.4c-5.1 0-9.3 3.34-9.3 7.47 0 3.7 3.3 6.8 7.74 7.36l-.84 2.86c-.08.26.22.47.45.32l3.57-2.3h.38c5.1 0 9.3-3.34 9.3-7.47S17.1 3.4 12 3.4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

const CONTACT_METHODS = [
  {
    id: 'mail',
    href: `mailto:${EMAIL}`,
    label: { ja: 'メール', en: 'Email' },
    value: { ja: EMAIL, en: EMAIL },
    Icon: MailIcon,
  },
  {
    id: 'instagram',
    href: 'https://www.instagram.com/yoshidont_mind/',
    label: { ja: 'Instagram', en: 'Instagram' },
    value: { ja: '@yoshidont_mind', en: '@yoshidont_mind' },
    Icon: InstagramIcon,
  },
  {
    id: 'line',
    href: 'https://line.me/ti/p/qBCcqeVEE0',
    label: { ja: 'LINE', en: 'LINE' },
    value: { ja: 'LINEで連絡する', en: 'Message me on LINE' },
    Icon: LineIcon,
  },
];

function ContactSection({ language }) {
  const [bodyRef, isVisible] = useInView();
  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (copyResetTimer.current) {
        window.clearTimeout(copyResetTimer.current);
      }
    };
  }, []);

  const copy =
    language === 'ja'
      ? {
          label: 'CONTACT',
          title: '一緒につくりましょう。',
          lead: '本サイトをご覧いただきありがとうございます。お仕事のご依頼・ご相談は、メール、Instagram、LINEのお好きな方法でどうぞ。',
          copyEmail: 'メールアドレスをコピー',
          copied: 'コピーしました',
        }
      : {
          label: 'CONTACT',
          title: "Let's build something.",
          lead: 'Thank you for visiting this portfolio. For project inquiries and consultations, reach out via email, Instagram, or LINE.',
          copyEmail: 'Copy email address',
          copied: 'Copied',
        };

  const handleCopyEmail = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(EMAIL);
        setCopied(true);
        if (copyResetTimer.current) {
          window.clearTimeout(copyResetTimer.current);
        }
        copyResetTimer.current = window.setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      // Clipboard unavailable — the address is still selectable text.
    }
  };

  return (
    <section id="contact" className="contact-section" aria-label={copy.label}>
      <SectionHeader index="09" label={copy.label} title={copy.title} lead={copy.lead} />

      <div ref={bodyRef} className={`contact-body reveal ${isVisible ? 'is-visible' : ''}`}>
        <div className="contact-email">
          <a href={`mailto:${EMAIL}`} className="contact-email__address mono">
            {EMAIL}
          </a>
          <button
            type="button"
            className="contact-email__copy chip"
            onClick={handleCopyEmail}
            aria-live="polite"
          >
            {copied ? `✓ ${copy.copied}` : copy.copyEmail}
          </button>
        </div>

        <div className="contact-grid">
          {CONTACT_METHODS.map((method) => {
            const Icon = method.Icon;
            const isExternal = !method.href.startsWith('mailto:');

            return (
              <a
                key={method.id}
                className="contact-card"
                href={method.href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                aria-label={`${method.label[language]}: ${method.value[language]}`}
              >
                <span className="contact-card__icon" aria-hidden="true">
                  <Icon />
                </span>
                <span className="contact-card__meta">
                  <span className="contact-card__label mono">{method.label[language]}</span>
                  <span className="contact-card__value">{method.value[language]}</span>
                </span>
                <span className="contact-card__arrow" aria-hidden="true">
                  ↗
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default ContactSection;
